/**
 * /admin/marketing/refresh — generate fresh marketing materials from
 * current app state.
 *
 * One big "Regenerate" button → Claude Sonnet drafts a complete
 * marketing pack (elevator pitch in 3 lengths, press release, tweet
 * thread, LinkedIn post, demo-video script, persona email templates,
 * investor one-pager). Tone is fixed to aspirational/luxury — matches
 * the gold-on-black brand language.
 *
 * Each section renders in its own card with a copy-to-clipboard button.
 * History tab shows previous generations (versioned in
 * `marketing_packs` collection) so you can compare what you said in
 * the launch press kit vs what you'd say today.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeftIcon as ArrowLeft, SparkleIcon as Sparkle, CopyIcon as Copy, MegaphoneIcon as Megaphone, FileTextIcon as FileText, TwitterLogoIcon as TwitterLogo, LinkedinLogoIcon as LinkedinLogo, VideoCameraIcon as VideoCamera, EnvelopeSimpleIcon as EnvelopeSimple, BriefcaseIcon as Briefcase, ClockClockwiseIcon as ClockClockwise, PlayIcon as Play, StopIcon as Stop, CheckCircleIcon as CheckCircle, WarningCircleIcon as WarningCircle, ClockIcon as Clock, ArrowsClockwiseIcon as ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const formatRuntime = (sec) => {
  if (sec == null) return "—";
  const s = Math.max(0, Math.floor(sec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};
const formatRelative = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

export default function AdminMarketingRefresh() {
  const [pack, setPack] = useState(null);
  const [context, setContext] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [phase, setPhase] = useState(""); // "queued" | "gathering_context" | "calling_llm" | "done"
  const [history, setHistory] = useState([]);

  // Lead-angle picker — defaults to "" (random rotation). Each angle frames
  // the pitch around a different thesis so consecutive packs feel distinct.
  const [leadAngle, setLeadAngle] = useState("");
  const [angleCatalog, setAngleCatalog] = useState([]);

  useEffect(() => {
    api.get("/admin/marketing/refresh/lead-angles")
      .then((r) => setAngleCatalog(r.data?.angles || []))
      .catch(() => setAngleCatalog([]));
  }, []);

  // ----- Video re-record state -----
  const [videoJob, setVideoJob] = useState(null);     // current/last job row
  const [videoHistory, setVideoHistory] = useState([]); // past jobs
  const [clipFreshness, setClipFreshness] = useState([]); // per-clip mtimes

  const loadVideoStatus = useCallback(async () => {
    try {
      const [s, h, f] = await Promise.all([
        api.get("/admin/marketing/rerecord/status"),
        api.get("/admin/marketing/rerecord/history?limit=5"),
        api.get("/admin/marketing/clips/freshness"),
      ]);
      setVideoJob(s.data?.job || null);
      setVideoHistory(h.data?.items || []);
      setClipFreshness(f.data?.clips || []);
    } catch { /* fine, just keep empty */ }
  }, []);

  const startRerecord = async () => {
    try {
      const r = await api.post("/admin/marketing/rerecord");
      if (r.data?.already_running) {
        toast.info("A re-record is already running.");
      } else {
        toast.success("Re-recording started — usually takes 2–4 minutes.");
      }
      loadVideoStatus();
    } catch (e) {
      // 503 → on-pod recording unavailable (production). Direct the
      // admin to the GitHub Actions workflow which IS supported.
      if (e?.response?.status === 503) {
        const detail = e.response.data?.detail || "";
        const urlMatch = detail.match(/https:\/\/github\.com\/[^\s]+/);
        if (urlMatch) {
          window.open(urlMatch[0], "_blank", "noopener,noreferrer");
          toast.info("On-pod recording isn't available here — opened the GitHub Actions workflow in a new tab. Click \"Run workflow\" there.");
          return;
        }
      }
      toast.error(e?.response?.data?.detail || "Couldn't start re-record.");
    }
  };

  const cancelRerecord = async () => {
    try {
      const r = await api.post("/admin/marketing/rerecord/cancel", {});
      if (r.data?.cancelled) {
        toast.success(r.data.killed_subprocess ? "Subprocess killed; job cancelled." : "Job marked cancelled (subprocess wasn't running).");
      } else {
        toast.info(r.data?.reason || "No active job to cancel.");
      }
      loadVideoStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/admin/marketing/refresh/history?limit=10");
      setHistory(r.data?.items || []);
    } catch { /* fine, just an empty history list */ }
  }, []);

  useEffect(() => { loadHistory(); loadVideoStatus(); }, [loadHistory, loadVideoStatus]);

  // Auto-poll the video job while it's running (mirrors the AI-pack
  // polling cadence). Stops as soon as status leaves "running"/"queued".
  useEffect(() => {
    const isLive = videoJob && ["queued", "running"].includes(videoJob.status);
    if (!isLive) return;
    const t = setInterval(loadVideoStatus, 5000);
    return () => clearInterval(t);
  }, [videoJob, loadVideoStatus]);

  const fullRefresh = async () => {
    // Part 3: one-button "Full refresh" — kicks off BOTH the AI pack
    // and the video re-recording in parallel.
    await Promise.all([generate(), startRerecord()]);
  };

  const generate = async () => {
    setGenerating(true);
    setPhase("queued");
    try {
      // Kick off the job — returns immediately with a job_id.
      const start = await api.post("/admin/marketing/refresh/generate", {
        lead_angle: leadAngle || null,
      });
      const jobId = start.data.job_id;
      if (!jobId) throw new Error("No job id returned");

      // Poll every 3 seconds for up to 4 minutes. Claude Sonnet
      // typically finishes around 60-90s but we leave headroom.
      const deadline = Date.now() + 4 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 3000));
        let job;
        try {
          job = (await api.get(`/admin/marketing/refresh/jobs/${jobId}`)).data;
        } catch (e) {
          // Transient — keep polling unless it's a 404.
          if (e?.response?.status === 404) throw e;
          continue;
        }
        setPhase(job.phase || "calling_llm");
        if (job.status === "complete") {
          setPack(job.pack);
          setContext(job.context);
          setGeneratedAt(job.completed_at);
          toast.success("Fresh marketing pack ready");
          loadHistory();
          return;
        }
        if (job.status === "error") {
          toast.error(job.error || "AI couldn't finish. Try regenerating.");
          return;
        }
      }
      toast.error("Took longer than 4 minutes — try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't start generation.");
    } finally {
      setGenerating(false);
      setPhase("");
    }
  };

  const loadOldPack = async (packId) => {
    try {
      const r = await api.get(`/admin/marketing/refresh/${packId}`);
      setPack(r.data.pack);
      setContext(r.data.context_snapshot);
      setGeneratedAt(r.data.completed_at || r.data.generated_at);
      toast.success("Loaded previous generation");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-marketing-refresh-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-[#6B6B6B] hover:text-ink text-sm mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to admin
      </Link>
      <header className="mb-6">
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight text-ink">Marketing refresh</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          One click. Claude reads your live stats, your current pricing, and your last few shipped features — then drafts a complete marketing pack: elevator pitch, press release, tweet thread, LinkedIn post, demo-video script, persona email templates, and an investor one-pager. Tone matches your gold-on-black brand language. You edit, you publish — never auto-shipped to the live site.
        </p>
      </header>

      {/* Marketing video panel — sits ABOVE the AI pack so the
          whole "marketing freshness" picture is on one screen. */}
      <VideoSection
        job={videoJob}
        history={videoHistory}
        clips={clipFreshness}
        onStart={startRerecord}
        onCancel={cancelRerecord}
        onRefresh={loadVideoStatus}
      />

      {!pack && !generating && (
        <section className="bg-gradient-to-br from-[#FBF7EC] to-white border border-[#E8D9A6] rounded-2xl p-8 text-center" data-testid="generate-cta-section">
          <span className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-ink text-gold mb-4">
            <Sparkle size={28} weight="duotone" />
          </span>
          <h2 className="font-spectral text-2xl text-ink mb-2">Ready when you are</h2>
          <p className="text-sm text-[#6B6B6B] mb-5 max-w-md mx-auto">
            Takes about 30 seconds. Costs roughly twenty cents in LLM tokens. The output stays as drafts you can copy and edit — nothing auto-publishes.
          </p>
          {angleCatalog.length > 0 && (
            <div className="flex flex-col items-center gap-1.5 mb-4 max-w-sm mx-auto">
              <label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] font-semibold">
                Lead angle for this pack
              </label>
              <Select value={leadAngle || "random"} onValueChange={(v) => setLeadAngle(v === "random" ? "" : v)}>
                <SelectTrigger data-testid="lead-angle-select" className="rounded-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">🎲 Surprise me (rotate)</SelectItem>
                  {angleCatalog.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`lead-angle-${a.id}`}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {leadAngle && (
                <p className="text-[11px] text-[#6B6B6B] italic max-w-sm text-center">
                  {angleCatalog.find((a) => a.id === leadAngle)?.thesis}
                </p>
              )}
            </div>
          )}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button
              onClick={generate}
              className="bg-ink hover:bg-crimson text-white rounded-full h-12 px-7 text-[15px]"
              data-testid="generate-marketing-pack-btn"
            >
              <Sparkle size={16} weight="duotone" className="mr-2" />
              Regenerate AI marketing pack
            </Button>
            <Button
              onClick={fullRefresh}
              disabled={generating || (videoJob && ["queued", "running"].includes(videoJob.status))}
              variant="outline"
              className="rounded-full h-12 px-6 text-[14px] border-gold-dark text-gold-dark hover:bg-gold/10"
              data-testid="full-refresh-btn"
              title="Regenerate AI copy AND re-record all 6 product clips in parallel"
            >
              <ArrowsClockwise size={14} weight="bold" className="mr-2" />
              Regenerate everything (AI pack + videos)
            </Button>
          </div>
        </section>
      )}

      {generating && (
        <section className="bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center" data-testid="generating-section">
          <Sparkle size={32} weight="duotone" className="text-gold-dark mb-3 mx-auto animate-pulse" />
          <p className="font-spectral text-xl text-ink">Drafting your marketing pack…</p>
          <p className="text-[12px] text-[#6B6B6B] mt-2">
            {phase === "gathering_context" && "Pulling your live stats…"}
            {phase === "calling_llm" && "Claude is writing — this takes 60-90 seconds."}
            {(phase === "queued" || !phase) && "Starting up…"}
          </p>
        </section>
      )}

      {pack && (
        <PackView
          pack={pack}
          context={context}
          generatedAt={generatedAt}
          onRegen={generate}
          regenerating={generating}
          leadAngle={leadAngle}
          setLeadAngle={setLeadAngle}
          angleCatalog={angleCatalog}
        />
      )}

      {history.length > 0 && (
        <HistorySection history={history} onLoad={loadOldPack} />
      )}
    </div>
  );
}

function PackView({ pack, context, generatedAt, onRegen, regenerating, leadAngle, setLeadAngle, angleCatalog }) {
  const usedAngleId = context?.lead_angle?.id;
  const usedAngle = angleCatalog.find((a) => a.id === usedAngleId);
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-[12px] text-[#6B6B6B]">
          Generated <b className="text-ink">{new Date(generatedAt).toLocaleString()}</b>
          {context?.stats && (
            <span className="ml-2">· grounded in {context.stats.kennels} kennels · {context.stats.dogs_registered} dogs · {context.stats.distinct_breeds} breeds</span>
          )}
          {usedAngle && (
            <span className="ml-2">· lead angle: <b className="text-ink">{usedAngle.label}</b></span>
          )}
        </p>
        <div className="flex items-center gap-2">
          {angleCatalog.length > 0 && (
            <Select value={leadAngle || "random"} onValueChange={(v) => setLeadAngle(v === "random" ? "" : v)}>
              <SelectTrigger className="rounded-full h-9 text-[12px] bg-white" data-testid="lead-angle-select-pack">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="random">🎲 Next: surprise me</SelectItem>
                {angleCatalog.map((a) => (
                  <SelectItem key={a.id} value={a.id}>Next: {a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={onRegen}
            disabled={regenerating}
            variant="outline"
            className="rounded-full h-9 px-4 text-[12px]"
            data-testid="regenerate-btn"
          >
            <Sparkle size={12} weight="duotone" className="mr-1.5" /> Regenerate
          </Button>
        </div>
      </div>

      <Tabs defaultValue="pitch" className="w-full">
        <TabsList className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-full p-1 inline-flex flex-wrap gap-1 mb-6 text-[12px] h-auto">
          <PackTab v="pitch" icon={Megaphone} label="Elevator pitch" />
          <PackTab v="press" icon={FileText} label="Press release" />
          <PackTab v="tweet" icon={TwitterLogo} label="Tweet thread" />
          <PackTab v="linkedin" icon={LinkedinLogo} label="LinkedIn" />
          <PackTab v="video" icon={VideoCamera} label="Video script" />
          <PackTab v="emails" icon={EnvelopeSimple} label="Emails" />
          <PackTab v="onepager" icon={Briefcase} label="Investor 1-pager" />
        </TabsList>

        <TabsContent value="pitch">
          <Card title="15-second hook"   body={pack.elevator_pitch?.fifteen_sec} />
          <Card title="60-second pitch"  body={pack.elevator_pitch?.sixty_sec} />
          <Card title="3-minute pitch"   body={pack.elevator_pitch?.three_min} />
        </TabsContent>

        <TabsContent value="press">
          <Card title={pack.press_release?.headline || "Press release"}
                subtitle={pack.press_release?.dateline}
                body={pack.press_release?.body} />
        </TabsContent>

        <TabsContent value="tweet">
          <div className="space-y-3">
            {(pack.tweet_thread || []).map((t, i) => (
              <Card key={i} title={`Tweet ${i + 1}/${pack.tweet_thread.length}`} body={t} small />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="linkedin">
          <Card title="LinkedIn post" body={pack.linkedin_post} />
        </TabsContent>

        <TabsContent value="video">
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-3">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1">Target duration</p>
            <p className="font-spectral text-xl text-ink mb-4">{pack.demo_video_script?.duration_target || "—"}</p>
            <div className="space-y-3">
              {(pack.demo_video_script?.scenes || []).map((scene, i) => (
                <div key={i} className="border-l-2 border-gold pl-4">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-gold-dark">{scene.timestamp}</p>
                  <p className="text-[12px] text-[#6B6B6B] mt-1 italic"><b className="not-italic text-ink">Visual:</b> {scene.visual}</p>
                  <p className="text-[13px] text-ink mt-1.5 leading-relaxed"><b>Voiceover:</b> {scene.voiceover}</p>
                </div>
              ))}
            </div>
            <CopyButton text={JSON.stringify(pack.demo_video_script, null, 2)} className="mt-4" label="Copy full script (JSON)" />
          </div>
        </TabsContent>

        <TabsContent value="emails">
          {Object.entries(pack.email_templates || {}).map(([persona, email]) => (
            <Card
              key={persona}
              title={`${persona.charAt(0).toUpperCase() + persona.slice(1)} email`}
              subtitle={`Subject: ${email.subject}`}
              body={email.body}
            />
          ))}
        </TabsContent>

        <TabsContent value="onepager">
          <Card title="Tagline"        body={pack.investor_one_pager?.tagline} small />
          <Card title="The problem"    body={pack.investor_one_pager?.the_problem} />
          <Card title="The solution"   body={pack.investor_one_pager?.the_solution} />
          <Card title="The traction"   body={pack.investor_one_pager?.the_traction} />
          <Card title="The moat"       body={pack.investor_one_pager?.the_moat} />
          <Card title="The ask"        body={pack.investor_one_pager?.the_ask} />
        </TabsContent>
      </Tabs>
    </>
  );
}

function PackTab({ v, icon: Icon, label }) {
  return (
    <TabsTrigger value={v} className="rounded-full px-3 py-1.5 data-[state=active]:bg-ink data-[state=active]:text-white text-[11px]">
      <Icon size={12} weight="bold" className="mr-1.5" /> {label}
    </TabsTrigger>
  );
}

function Card({ title, subtitle, body, small }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-3" data-testid={`pack-card-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-spectral text-lg tracking-tight text-ink">{title}</h3>
          {subtitle && <p className="text-[11px] text-[#6B6B6B] mt-0.5">{subtitle}</p>}
        </div>
        <CopyButton text={body} />
      </div>
      <p className={`whitespace-pre-wrap text-[#3F3F3F] leading-relaxed ${small ? "text-[13px]" : "text-[14px]"}`}>
        {body}
      </p>
    </div>
  );
}

function CopyButton({ text, label = "Copy", className = "" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard blocked. Select and copy manually.");
    }
  };
  return (
    <button
      onClick={copy}
      type="button"
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#6B6B6B] hover:text-ink uppercase tracking-wider px-2 py-1 rounded-full hover:bg-[#FAFAFA] flex-shrink-0 ${className}`}
      data-testid="copy-btn"
    >
      <Copy size={12} weight="bold" />
      {copied ? "Copied" : label}
    </button>
  );
}

function HistorySection({ history, onLoad }) {  return (
    <section className="mt-10 bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="history-section">
      <h2 className="font-spectral text-xl tracking-tight text-ink mb-3 flex items-center gap-2">
        <ClockClockwise size={16} weight="duotone" className="text-[#6B6B6B]" />
        Previous generations
      </h2>
      <div className="space-y-2">
        {history.map((row) => {
          const stats = row.context_snapshot?.stats || {};
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => onLoad(row.id)}
              className="w-full text-left flex items-center justify-between gap-3 rounded-xl border border-[#E5E5E5] hover:border-ink/40 px-3 py-2.5 transition-colors"
              data-testid={`history-row-${row.id}`}
            >
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-ink">{new Date(row.generated_at).toLocaleString()}</p>
                <p className="text-[11px] text-[#6B6B6B] mt-0.5">
                  {stats.kennels || 0} kennels · {stats.dogs_registered || 0} dogs · {stats.distinct_breeds || 0} breeds
                </p>
              </div>
              <Badge className="bg-[#FBF7EC] text-gold-dark border-0 text-[10px]">Load</Badge>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ============================================================
 * Video re-record panel — Part 2 of the marketing-refresh unification.
 * Lives above the AI pack so the admin sees the whole "marketing
 * freshness" picture on one screen. Wires the existing
 * /api/admin/marketing/rerecord/{status,history,cancel} endpoints
 * with timestamps, runtime, last-log preview, and a Cancel button.
 * ============================================================ */
function VideoSection({ job, history, clips, onStart, onCancel, onRefresh }) {
  const isLive = job && ["queued", "running"].includes(job.status);
  const tone = job?.status === "succeeded" ? "ok"
             : job?.status === "failed"    ? "err"
             : job?.status === "stale"     ? "warn"
             : job?.status === "cancelled" ? "warn"
             : isLive ? "live" : "idle";
  const toneCls = {
    ok:   "border-[#DCFCE7] bg-[#F0FDF4]",
    err:  "border-[#FEE2E2] bg-[#FEF2F2]",
    warn: "border-[#FEF3C7] bg-[#FFFBEB]",
    live: "border-[#E8D9A6] bg-[#FBF7EC]",
    idle: "border-[#E5E5E5] bg-white",
  }[tone];

  return (
    <section className={`rounded-2xl border ${toneCls} p-5 mb-6`} data-testid="video-section">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="rounded-full bg-ink text-gold h-12 w-12 inline-flex items-center justify-center flex-shrink-0">
          <VideoCamera size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-spectral text-xl tracking-tight text-ink">Marketing videos · 6 product clips</h2>
          <p className="text-[12px] text-[#6B6B6B] mt-0.5">Playwright re-records every clip against the current live UI. Usually finishes in 2–4 minutes.</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isLive && (
            <Button
              onClick={onStart}
              className="bg-ink hover:bg-crimson text-white rounded-full h-9 px-4 text-[12px]"
              data-testid="video-rerecord-btn"
            >
              <Play size={12} weight="bold" className="mr-1.5" /> Re-record all
            </Button>
          )}
          {isLive && (
            <Button
              onClick={onCancel}
              variant="outline"
              className="border-crimson text-crimson hover:bg-crimson/5 rounded-full h-9 px-4 text-[12px]"
              data-testid="video-cancel-btn"
            >
              <Stop size={12} weight="bold" className="mr-1.5" /> Cancel
            </Button>
          )}
          <button
            onClick={onRefresh}
            type="button"
            className="text-[#6B6B6B] hover:text-ink p-1.5 rounded-full hover:bg-white/60"
            title="Refresh status now"
            data-testid="video-refresh-btn"
          >
            <ArrowsClockwise size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Current job status — only show if we have a job row at all. */}
      {job && <VideoJobStatus job={job} />}

      {/* Per-clip freshness grid */}
      {clips.length > 0 && <ClipFreshnessGrid clips={clips} />}

      {/* History */}
      {history.length > 0 && <VideoHistoryList history={history} />}

      {/* Option B — drag-and-drop upload fallback for when GH Actions
          isn't an option, or for one-off corrections to a single clip. */}
      <ClipUploadDropzone onUploaded={onRefresh} />
    </section>
  );
}

function ClipUploadDropzone({ onUploaded }) {
  const CLIP_IDS = [
    "01_register_dog", "02_intelligent_breeding", "03_buy_without_burned",
    "04_transport_marketplace", "05_breeder_storefront", "06_dog_log_care",
  ];
  const [selectedClipId, setSelectedClipId] = useState(CLIP_IDS[0]);
  const [uploading, setUploading] = useState(null); // filename in progress
  const [dragging, setDragging] = useState(false);

  const uploadOne = async (file, clipId) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error(`${file.name}: file is over 100MB.`);
      return;
    }
    setUploading(file.name);
    try {
      await api.post(
        `/admin/marketing/clips/upload?clip_id=${encodeURIComponent(clipId)}`,
        file,
        { headers: { "Content-Type": file.type || "video/mp4" } }
      );
      toast.success(`Uploaded ${clipId} (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Upload failed for ${clipId}`);
    } finally {
      setUploading(null);
    }
  };

  const onFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => /\.(mp4|webm)$/i.test(f.name));
    if (!list.length) {
      toast.error("Only .mp4 or .webm files are accepted.");
      return;
    }
    // If files are named exactly like the clip ids (e.g. "01_register_dog.mp4"),
    // auto-route each to its slot. Otherwise upload all selected files to
    // the dropdown-selected clip slot (handy for re-recording just one).
    for (const f of list) {
      const stem = f.name.replace(/\.(mp4|webm)$/i, "");
      const auto = CLIP_IDS.includes(stem) ? stem : null;
      // eslint-disable-next-line no-await-in-loop -- serial upload keeps memory predictable
      await uploadOne(f, auto || selectedClipId);
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-black/5">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">
        Or upload manually
      </p>
      <p className="text-[11px] text-[#6B6B6B] mb-3 leading-relaxed">
        Drag-and-drop MP4 / WebM files here. If a file is named exactly like a clip id
        (e.g. <span className="font-mono text-ink">01_register_dog.mp4</span>) it routes to that slot automatically.
        Otherwise it uploads to the slot you pick below.
      </p>
      <div className="flex items-center gap-2 mb-3">
        <label className="text-[11px] text-[#6B6B6B] font-semibold">Default slot:</label>
        <select
          value={selectedClipId}
          onChange={(e) => setSelectedClipId(e.target.value)}
          className="text-[12px] rounded-full border border-[#E5E5E5] px-3 py-1.5 bg-white"
          data-testid="upload-target-clip"
        >
          {CLIP_IDS.map((id) => (
            <option key={id} value={id}>{id}</option>
          ))}
        </select>
      </div>
      <label
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={(e) => {
          e.preventDefault(); setDragging(false);
          onFiles(e.dataTransfer.files);
        }}
        className={`block rounded-xl border-2 border-dashed px-5 py-6 text-center cursor-pointer transition-colors ${
          dragging ? "border-gold-dark bg-[#FBF7EC]" : "border-[#E5E5E5] hover:border-ink/40 bg-white"
        }`}
        data-testid="upload-dropzone"
      >
        <input
          type="file"
          accept="video/mp4,video/webm,.mp4,.webm"
          multiple
          onChange={(e) => onFiles(e.target.files)}
          className="hidden"
          data-testid="upload-file-input"
        />
        {uploading ? (
          <p className="text-[13px] text-ink font-semibold">Uploading {uploading}…</p>
        ) : (
          <>
            <p className="text-[13px] text-ink font-semibold">Drop video files here</p>
            <p className="text-[11px] text-[#6B6B6B] mt-0.5">or click to browse · MP4 / WebM · ≤100MB each</p>
          </>
        )}
      </label>
    </div>
  );
}

function VideoJobStatus({ job }) {
  const isLive = ["queued", "running"].includes(job.status);
  const StatusIcon = job.status === "succeeded" ? CheckCircle
                   : job.status === "failed"    ? WarningCircle
                   : job.status === "stale"     ? WarningCircle
                   : job.status === "cancelled" ? Stop
                   : Clock;
  const statusColor = job.status === "succeeded" ? "text-[#15803D]"
                    : job.status === "failed" || job.status === "stale" ? "text-crimson"
                    : job.status === "cancelled" ? "text-[#A16207]"
                    : "text-gold-dark";
  return (
    <div className="mt-4 pt-4 border-t border-black/5 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
      <Metric icon={StatusIcon} iconClass={statusColor} label="Status" value={
        <>
          <span className={`font-semibold capitalize ${statusColor}`}>{job.status}</span>
          {isLive && job.phase && <span className="text-[#6B6B6B] ml-1">· {job.phase}</span>}
        </>
      } />
      <Metric icon={Clock} label="Started" value={formatRelative(job.started_at || job.queued_at)} sub={job.started_at || job.queued_at ? new Date(job.started_at || job.queued_at).toLocaleString() : null} />
      <Metric icon={ClockClockwise} label="Runtime" value={formatRuntime(job.runtime_seconds)} sub={job.finished_at ? `finished ${formatRelative(job.finished_at)}` : isLive ? "still running" : null} />
      <Metric icon={FileText} label="Last log line" value={
        <span className="text-[11px] text-[#3F3F3F] font-mono line-clamp-1" title={job.log_tail || ""}>
          {((job.log_tail || "").split("\n").filter(Boolean).slice(-1)[0]) || "—"}
        </span>
      } />
      {job.error && (
        <div className="col-span-full bg-white border border-[#FECACA] rounded-lg p-3 text-[12px] text-crimson">
          <b>Error:</b> {job.error}
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, iconClass = "text-[#6B6B6B]", label, value, sub }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1 flex items-center gap-1">
        <Icon size={10} weight="bold" className={iconClass} /> {label}
      </p>
      <div className="text-[13px] text-ink">{value}</div>
      {sub && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{sub}</p>}
    </div>
  );
}

function ClipFreshnessGrid({ clips }) {
  return (
    <div className="mt-5 pt-4 border-t border-black/5">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">Per-clip freshness</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {clips.map((c) => {
          const stale = c.age_days != null && c.age_days >= 14;
          const missing = !c.has_mp4;
          return (
            <div
              key={c.id}
              className={`rounded-lg border px-3 py-2 text-[12px] ${missing ? "border-[#FECACA] bg-[#FEF2F2]" : stale ? "border-[#FEF3C7] bg-[#FFFBEB]" : "border-[#E5E5E5] bg-white"}`}
              data-testid={`clip-freshness-${c.id}`}
            >
              <p className="font-semibold text-ink text-[12px] truncate">{c.name}</p>
              <p className="text-[10px] text-[#6B6B6B] mt-0.5">
                {missing ? "missing" : c.newest_modified_at ? `${formatRelative(c.newest_modified_at)} · ${c.age_days}d` : "—"}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VideoHistoryList({ history }) {
  return (
    <div className="mt-5 pt-4 border-t border-black/5">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">Recent jobs</p>
      <div className="space-y-1">
        {history.map((j) => {
          const StatusIcon = j.status === "succeeded" ? CheckCircle
                           : j.status === "failed" || j.status === "stale" ? WarningCircle
                           : j.status === "cancelled" ? Stop
                           : Clock;
          const statusColor = j.status === "succeeded" ? "text-[#15803D]"
                            : j.status === "failed" || j.status === "stale" ? "text-crimson"
                            : j.status === "cancelled" ? "text-[#A16207]"
                            : "text-gold-dark";
          return (
            <div key={j.job_id} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/60 text-[12px]">
              <StatusIcon size={14} weight="bold" className={statusColor} />
              <span className="font-mono text-[11px] text-[#6B6B6B] truncate flex-shrink-0">{j.job_id.slice(0, 8)}</span>
              <span className={`capitalize font-semibold ${statusColor}`}>{j.status}</span>
              <span className="text-[#6B6B6B] ml-auto flex-shrink-0">{formatRelative(j.queued_at)}</span>
              {j.created_by_email && <span className="text-[#9CA3AF] text-[10px] hidden sm:inline">· {j.created_by_email}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

